import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { format } from 'date-fns';
import { Calendar, Loader2, Printer } from 'lucide-react';
import { shiftApi } from '@/services/api';
import type { DayZReport } from '@/types';
import { browserPrintDayZReport } from '@/utils/shiftReceiptPrint';
import { formatMoney } from '@/utils/currency';
import { downloadCsv } from '@/utils/csv';
import ReportExportButton from '@/components/reports/ReportExportButton';

export default function ZDayReportSection() {
  const [date, setDate] = useState(format(new Date(), 'yyyy-MM-dd'));

  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ['dayZReport', date],
    queryFn: async () => {
      const res = await shiftApi.getDayZReport(date);
      return res.data.message as DayZReport;
    },
  });

  const handleExport = () => {
    if (!data?.shifts?.length) return;
    downloadCsv(
      `Z_Day_${date}.csv`,
      [
        { key: 'z_number', label: 'Z#' },
        { key: 'name', label: 'Shift' },
        { key: 'cashier_name', label: 'Cashier' },
        { key: 'pocket_name', label: 'Drawer' },
        { key: 'total_sales', label: 'Sales' },
        { key: 'closing_amount', label: 'Counted' },
        { key: 'expected_cash', label: 'Expected' },
        { key: 'difference', label: 'Variance' },
      ],
      data.shifts.map((s) => ({
        ...s,
        cashier_name: s.cashier_name || s.cashier,
      })),
    );
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row gap-3 justify-between items-start sm:items-center bg-white p-4 rounded-xl border border-gray-100 shadow-sm">
        <div className="flex items-center gap-2">
          <Calendar className="w-5 h-5 text-gray-400" />
          <input
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            className="border-gray-200 rounded-lg text-sm"
          />
          <button
            type="button"
            onClick={() => void refetch()}
            className="text-sm px-3 py-1.5 border border-gray-200 rounded-lg hover:bg-gray-50"
          >
            Refresh
          </button>
        </div>
        <div className="flex gap-2">
          <ReportExportButton onClick={handleExport} disabled={!data?.shifts?.length || isLoading} />
          <button
            type="button"
            disabled={!data}
            onClick={() => data && browserPrintDayZReport(data)}
            className="text-sm px-3 py-2 bg-gray-900 text-white rounded-lg hover:bg-gray-700 disabled:opacity-40 inline-flex items-center gap-2"
          >
            <Printer className="w-4 h-4" /> Print Day Z
          </button>
        </div>
      </div>

      {isLoading && (
        <p className="text-sm text-gray-500 flex items-center gap-2">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading day Z…
        </p>
      )}

      {isError && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl text-sm">
          {(error as { response?: { data?: { message?: string } } })?.response?.data?.message ||
            'Failed to load day Z report'}
        </div>
      )}

      {data && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {[
              ['Shifts', String(data.shift_count)],
              ['Invoices', String(data.invoice_count)],
              ['Total sales', formatMoney(data.total_sales)],
              ['Variance (sum)', formatMoney(data.difference)],
            ].map(([label, value]) => (
              <div key={label} className="bg-white p-4 rounded-xl border border-gray-100 shadow-sm">
                <p className="text-sm text-gray-500">{label}</p>
                <p className="text-lg font-semibold text-gray-900 mt-1">{value}</p>
              </div>
            ))}
          </div>

          <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 text-gray-500">
                <tr>
                  <th className="text-left px-3 py-2">Z#</th>
                  <th className="text-left px-3 py-2">Cashier</th>
                  <th className="text-left px-3 py-2">Drawer</th>
                  <th className="text-right px-3 py-2">Sales</th>
                  <th className="text-right px-3 py-2">Counted</th>
                  <th className="text-right px-3 py-2">Expected</th>
                  <th className="text-right px-3 py-2">Variance</th>
                </tr>
              </thead>
              <tbody>
                {(data.shifts || []).map((s) => (
                  <tr key={s.name} className="border-t border-gray-50">
                    <td className="px-3 py-2">{s.z_number ?? '—'}</td>
                    <td className="px-3 py-2">{s.cashier_name || s.cashier}</td>
                    <td className="px-3 py-2">{s.pocket_name}</td>
                    <td className="px-3 py-2 text-right">{formatMoney(s.total_sales || 0)}</td>
                    <td className="px-3 py-2 text-right">{formatMoney(s.closing_amount || 0)}</td>
                    <td className="px-3 py-2 text-right">{formatMoney(s.expected_cash || 0)}</td>
                    <td className="px-3 py-2 text-right">{formatMoney(s.difference || 0)}</td>
                  </tr>
                ))}
                {!data.shifts?.length && (
                  <tr>
                    <td colSpan={7} className="px-3 py-6 text-center text-gray-400">
                      No closed shifts for this date
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
